/**
 * useMouseIdle — flip to `idle: true` after `delayMs` without user activity.
 *
 * Used to drive the Ops Dashboard's kiosk screensaver: after 5 minutes of
 * no mouse / keyboard / touch input, dim non-critical chrome so the tiles
 * and charts stand out on a distant wall-mounted TV.
 *
 * Signals watched:
 *   - mousemove, mousedown, wheel, touchstart, keydown
 *   - visibility change (leaving the tab counts as activity when returning)
 *
 * Any signal resets the timer and flips `idle` back to false immediately.
 * `disabled=true` short-circuits the whole thing so consumers can turn
 * the screensaver off without unmounting.
 */
import { useEffect, useRef, useState } from "react";

export function useMouseIdle({ delayMs = 5 * 60 * 1000, disabled = false } = {}) {
  const [idle, setIdle] = useState(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (disabled) {
      setIdle(false);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      return undefined;
    }
    const arm = () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      timerRef.current = setTimeout(() => setIdle(true), delayMs);
    };
    const wake = () => {
      if (idle) setIdle(false);
      arm();
    };
    // Listen on window so events bubble past inner focus traps.
    const events = ["mousemove", "mousedown", "wheel", "touchstart", "keydown"];
    events.forEach((e) => window.addEventListener(e, wake, { passive: true }));
    const onVisibility = () => { if (document.visibilityState === "visible") wake(); };
    document.addEventListener("visibilitychange", onVisibility);
    arm();
    return () => {
      events.forEach((e) => window.removeEventListener(e, wake));
      document.removeEventListener("visibilitychange", onVisibility);
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    };
  }, [delayMs, disabled, idle]);

  return idle;
}
